import { onScopeDispose, shallowRef, watch, type Ref, type ShallowRef } from 'vue';

/**
 * Vertical scroll position of the table's scroller, for consumers that react to
 * it (a "jump to first/last row" control, a sticky footer) without reaching into
 * the table's DOM (I-2172).
 */
export type NxTableScrollState = {
  /** Content is taller than the scroller by more than a pixel. */
  overflow: boolean;
  /** Pixels scrolled from the top (`scrollTop`). */
  offset: number;
  /** Pixels left until the end; 0 at the very end. */
  remaining: number;
};

const INITIAL: NxTableScrollState = { overflow: false, offset: 0, remaining: 0 };

/**
 * Tracks `scroller` on scroll and on size changes of the scroller and of its
 * content (rows mounting and unmounting change `scrollHeight` without resizing
 * the scroller). Measured at most once per frame; a new object only when a
 * value changed, so watchers do not fire on every scroll event.
 */
export function useScrollState(
  scroller: Readonly<Ref<HTMLElement | null | undefined>>
): Readonly<ShallowRef<NxTableScrollState>> {
  const state = shallowRef<NxTableScrollState>(INITIAL);
  let frame = 0;
  let observer: ResizeObserver | null = null;
  let bound: HTMLElement | null = null;

  function measure(): void {
    frame = 0;
    const el = bound;
    if (!el) return;
    const max = Math.max(0, el.scrollHeight - el.clientHeight);
    const next: NxTableScrollState = {
      overflow: max > 1,
      offset: el.scrollTop,
      remaining: Math.max(0, max - el.scrollTop),
    };
    const prev = state.value;
    if (
      prev.overflow !== next.overflow ||
      prev.offset !== next.offset ||
      prev.remaining !== next.remaining
    ) {
      state.value = next;
    }
  }

  function schedule(): void {
    if (frame || typeof requestAnimationFrame === 'undefined') return;
    frame = requestAnimationFrame(measure);
  }

  function unbind(): void {
    bound?.removeEventListener('scroll', schedule);
    observer?.disconnect();
    observer = null;
    bound = null;
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
  }

  watch(
    scroller,
    (el) => {
      unbind();
      if (!el) {
        state.value = INITIAL;
        return;
      }
      bound = el;
      el.addEventListener('scroll', schedule, { passive: true });
      if (typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(schedule);
        observer.observe(el);
        for (const child of Array.from(el.children)) observer.observe(child);
      }
      schedule();
    },
    { immediate: true, flush: 'post' }
  );

  onScopeDispose(unbind);

  return state;
}
